import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  LogOut, Phone, Mail, MapPin, Calendar, Clock, Trash2, Loader2, Inbox, RefreshCw,
} from "lucide-react";

const STATUSES = ["new", "contacted", "scheduled", "completed", "cancelled"];
const STATUS_STYLE = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  scheduled: "bg-purple-100 text-purple-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-slate-200 text-slate-600",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        api.get("/bookings", { params: filter !== "all" ? { status: filter } : {} }),
        api.get("/bookings/stats"),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Prowess" className="h-9 w-9 object-contain rounded" />
            <div>
              <div className="font-display font-bold text-slate-900 leading-none">Prowess Admin</div>
              <div className="text-xs text-slate-400">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-1.5" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-display font-bold text-slate-900 mb-6">Booking Requests</h1>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          <StatCard label="Total" value={stats.total ?? 0} active={filter === "all"} onClick={() => setFilter("all")} testid="stat-total" />
          {STATUSES.map((s) => (
            <StatCard key={s} label={s} value={stats[s] ?? 0} active={filter === s} onClick={() => setFilter(s)} testid={`stat-${s}`} />
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-lg border border-slate-200" data-testid="empty-state">
            <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No booking requests {filter !== "all" ? `with status "${filter}"` : "yet"}.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => (
              <div key={b.id} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`booking-row-${b.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display font-semibold text-lg text-slate-900">{b.name}</h3>
                      <Badge className={`${STATUS_STYLE[b.status]} border-0 capitalize`}>{b.status}</Badge>
                      <span className="text-sm font-medium text-accent">{b.service_type}</span>
                    </div>
                    <div className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-slate-600">
                      <a href={`tel:${b.phone}`} className="flex items-center gap-2 hover:text-accent"><Phone className="h-4 w-4 text-slate-400" /> {b.phone}</a>
                      {b.email && <a href={`mailto:${b.email}`} className="flex items-center gap-2 hover:text-accent"><Mail className="h-4 w-4 text-slate-400" /> {b.email}</a>}
                      {b.address && <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {b.address}</span>}
                      {(b.preferred_date || b.preferred_time) && (
                        <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-slate-400" /> {b.preferred_date} {b.preferred_time}</span>
                      )}
                    </div>
                    {b.details && <p className="mt-3 text-sm text-slate-500 bg-slate-50 rounded-md p-3">{b.details}</p>}
                    <p className="mt-2 text-xs text-slate-400 flex items-center gap-1.5"><Clock className="h-3 w-3" /> Submitted {fmtDate(b.created_at)}</p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                      <SelectTrigger className="w-[150px]" data-testid={`status-select-${b.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-btn-${b.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this booking?</AlertDialogTitle>
                          <AlertDialogDescription>This will permanently remove {b.name}'s request. This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={() => remove(b.id)} data-testid={`confirm-delete-${b.id}`}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-lg border p-4 transition-all ${active ? "border-accent bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
    >
      <div className="text-2xl font-display font-extrabold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 capitalize mt-0.5">{label}</div>
    </button>
  );
}
